import { useEffect, useRef, useState } from 'react';
import {
  Map3D,
  Marker3D,
  useMap3D,
} from '@vis.gl/react-google-maps';
import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_API_KEY_ENV_NAME } from '../../lib/mapsKey';

/**
 * GoogleMaps3DPicker — Photorealistic 3D Tiles building picker.
 * Activates Google's Map3DElement (alpha) for an immersive 3D city view.
 *
 * Required env var: REACT_APP_GOOGLE_MAPS_API_KEY (build-time, baked by CRA).
 * Legacy `REACT_APP_GOOGLE_MAPS_KEY` is read as a transitional fallback.
 *
 * Props:
 *   center: [lat, lng]              (current pin / camera target)
 *   onPickPosition: ({lat,lng}) =>  invoked when user clicks on the 3D map
 *
 * Notes:
 *  - The Map Tiles API must be enabled on the Google Cloud project to render
 *    Photorealistic 3D Tiles. Without it, you'll see a generic globe.
 *  - Photorealistic 3D Tiles cover ~2,500 cities worldwide. Outside coverage,
 *    Map3D still renders satellite + terrain.
 *  - Map3DElement is currently in alpha → wrap with try/catch to gracefully
 *    fall back to the parent's Mapbox renderer if the SDK fails to load.
 */

// Fly the camera to a target location with a smooth animation
const FlyToCamera = ({ center }) => {
  const map3d = useMap3D();
  const lastFlyKey = useRef(null);

  useEffect(() => {
    if (!map3d || !center) return;
    const key = `${center[0].toFixed(5)},${center[1].toFixed(5)}`;
    if (lastFlyKey.current === key) return;
    lastFlyKey.current = key;

    try {
      // flyCameraTo provides the cinematic Google Earth-like animation
      if (typeof map3d.flyCameraTo === 'function') {
        map3d.flyCameraTo({
          endCamera: {
            center: { lat: center[0], lng: center[1], altitude: 100 },
            tilt: 67,
            range: 500,
            heading: 0,
          },
          durationMillis: 1800,
        });
      } else {
        // Fallback to direct setters if flyCameraTo not available
        map3d.center = { lat: center[0], lng: center[1], altitude: 100 };
        map3d.range = 500;
        map3d.tilt = 67;
      }
    } catch {
      /* swallow — alpha API */
    }
  }, [map3d, center]);

  return null;
};

const ClickListener = ({ onPickPosition }) => {
  const map3d = useMap3D();
  useEffect(() => {
    if (!map3d) return;
    const handler = (e) => {
      try {
        const pos = e.position || e?.detail?.position;
        if (pos && typeof pos.lat === 'number' && typeof pos.lng === 'number') {
          onPickPosition({ lat: pos.lat, lng: pos.lng });
        }
      } catch {/* noop */}
    };
    map3d.addEventListener('gmp-click', handler);
    return () => map3d.removeEventListener('gmp-click', handler);
  }, [map3d, onPickPosition]);
  return null;
};

const GoogleMaps3DPicker = ({ center, onPickPosition }) => {
  const apiKey = GOOGLE_MAPS_API_KEY;
  const [loadError, setLoadError] = useState(null);

  if (!apiKey) {
    return (
      <div className="w-full h-full flex items-center justify-center text-xs text-amber-300/80 bg-black/30 rounded-xl">
        {GOOGLE_MAPS_API_KEY_ENV_NAME} non défini.
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="w-full h-full flex items-center justify-center text-xs text-red-300/80 bg-black/30 rounded-xl px-4 text-center">
        Échec du chargement Google Maps 3D — bascule en mode classique.
      </div>
    );
  }

  return (
    <div className="w-full h-full" data-testid="google-maps-3d-picker">
      <Map3D
          mode="HYBRID"
          defaultCenter={{
            lat: center?.[0] || 48.8566,
            lng: center?.[1] || 2.3522,
            altitude: 100,
          }}
          defaultRange={center ? 500 : 18000}
          defaultTilt={67}
          defaultHeading={0}
          style={{ width: '100%', height: '100%' }}
          data-testid="google-maps-3d-canvas"
        >
          <FlyToCamera center={center} />
          <ClickListener onPickPosition={onPickPosition} />
          {center && (
            <Marker3D
              position={{ lat: center[0], lng: center[1], altitude: 50 }}
              altitudeMode="RELATIVE_TO_GROUND"
              extruded
            />
          )}
        </Map3D>
    </div>
  );
};

export default GoogleMaps3DPicker;
